// ─────────────────────────────────────────────────────────────
// GÉNÉRATEUR DE QUITTANCES DE LOYER — IAM Business Group
// Nécessite : npm install jspdf
// ─────────────────────────────────────────────────────────────
import { jsPDF } from "jspdf";
import IAM_LOGO_B64 from "../assets/logoData";

const MOIS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

// ── Montant en toutes lettres (exigence d'une quittance légale) ──
const U = ["","un","deux","trois","quatre","cinq","six","sept","huit","neuf","dix","onze","douze","treize","quatorze","quinze","seize","dix-sept","dix-huit","dix-neuf"];
const D = ["","dix","vingt","trente","quarante","cinquante","soixante","soixante-dix","quatre-vingt","quatre-vingt-dix"];

function n2w(n) {
  if (n === 0) return "zéro";
  let s = "";
  const c = Math.floor(n / 100), r = n % 100;
  if (c) { s += c > 1 ? U[c] + " cent" : "cent"; if (c > 1 && r === 0) s += "s"; if (r) s += " "; }
  if (r) {
    if (r < 20) s += U[r];
    else {
      const d = Math.floor(r / 10), u = r % 10;
      if (d === 7 || d === 9) { s += D[d - 1] + (u === 1 && d === 7 ? " et " : "-") + U[10 + u]; }
      else { s += D[d]; if (u === 1 && d < 8) s += " et un"; else if (u) s += "-" + U[u]; else if (d === 8 && u === 0) s += "s"; }
    }
  }
  return s;
}

export function montantEnLettres(n) {
  n = Math.round(Math.abs(n));
  const m = Math.floor(n / 1e6), k = Math.floor((n % 1e6) / 1000), r = n % 1000;
  const p = [];
  if (m) p.push(m > 1 ? n2w(m) + " millions" : "un million");
  if (k) p.push(k > 1 ? n2w(k) + " mille" : "mille");
  if (r) p.push(n2w(r));
  return (p.join(" ") || "zéro") + " francs CFA";
}

function fmtMois(val) {
  if (!val) return "—";
  try { const [y, m] = val.split("-"); return `${MOIS[parseInt(m) - 1]} ${y}`; }
  catch (e) { return val; }
}

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}

// ─────────────────────────────────────────────────────────────
// genererQuittance({ payment, room })
//  payment : document Firestore du paiement confirmé
//  room    : document Firestore de la chambre (number, tenantName)
// Télécharge « Quittance_Q-2026-07-CH12-XXXX.pdf »
// ─────────────────────────────────────────────────────────────
export function genererQuittance({ payment, room }) {
  const p = payment || {};
  const r = room || {};
  const montant   = p.amount || r.loyer || 27500;
  const moisLabel = p.monthLabel || fmtMois(p.month);
  const numero    = `Q-${p.month || "XXXX-XX"}-CH${r.number || "?"}${p.id ? "-" + p.id.slice(-4).toUpperCase() : ""}`;

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, M = 18;
  const NAVY = [26, 58, 92], GREY = [90, 122, 154], LIGHT = [240, 244, 248];

  // ── En-tête ──
  try { doc.addImage(IAM_LOGO_B64.split(",")[1], "JPEG", M, 14, 26, 26); } catch (e) { /* logo optionnel */ }
  doc.setFont("helvetica", "bold").setFontSize(20).setTextColor(...NAVY);
  doc.text("QUITTANCE DE LOYER", W - M, 24, { align: "right" });
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(...GREY);
  doc.text(`N° ${numero}`, W - M, 31, { align: "right" });
  doc.text(`Émise le ${fmtDate(new Date())}`, W - M, 36.5, { align: "right" });

  doc.setDrawColor(...NAVY).setLineWidth(0.8).line(M, 45, W - M, 45);

  // ── Bloc bailleur ──
  let y = 54;
  doc.setFontSize(9).setTextColor(...GREY).setFont("helvetica", "normal").text("BAILLEUR", M, y);
  doc.setFontSize(11).setTextColor(30, 42, 58).setFont("helvetica", "bold");
  doc.text("IAM Business Group", M, y + 6);
  doc.setFont("helvetica", "normal").setFontSize(9.5).setTextColor(...GREY);
  doc.text("Mammibougou, à côté de l'IPR/IFRA de Katibougou", M, y + 11.5);
  doc.text("Koulikoro — Mali · WhatsApp : +223 77 72 77 71", M, y + 16.5);

  // ── Bloc locataire ──
  doc.setFontSize(9).setTextColor(...GREY).text("LOCATAIRE", W / 2 + 8, y);
  doc.setFontSize(11).setTextColor(30, 42, 58).setFont("helvetica", "bold");
  doc.text(r.tenantName || "—", W / 2 + 8, y + 6);
  doc.setFont("helvetica", "normal").setFontSize(9.5).setTextColor(...GREY);
  doc.text(`Chambre ${r.number || "—"}`, W / 2 + 8, y + 11.5);

  // ── Tableau récapitulatif ──
  y = 82;
  const rows = [
    ["Période concernée", moisLabel],
    ["Loyer mensuel", `${montant.toLocaleString("fr-FR")} FCFA`],
    ["Mode de paiement", p.method || "—"],
    ["Référence de transaction", p.reference || "—"],
    ["Paiement déclaré le", fmtDate(p.createdAt)],
    ["Paiement confirmé le", fmtDate(p.confirmedAt)],
  ];
  doc.setFontSize(10);
  rows.forEach((row, i) => {
    const ry = y + i * 10;
    if (i % 2 === 0) { doc.setFillColor(...LIGHT); doc.rect(M, ry - 6, W - 2 * M, 10, "F"); }
    doc.setTextColor(...GREY).setFont("helvetica", "normal").text(row[0], M + 4, ry);
    doc.setTextColor(30, 42, 58).setFont("helvetica", "bold").text(String(row[1]), W - M - 4, ry, { align: "right" });
  });

  // ── Montant en gros ──
  y = 152;
  doc.setFillColor(...NAVY).roundedRect(M, y, W - 2 * M, 22, 3, 3, "F");
  doc.setTextColor(255, 255, 255).setFont("helvetica", "normal").setFontSize(9);
  doc.text("MONTANT REÇU", M + 6, y + 8);
  doc.setFont("helvetica", "bold").setFontSize(17);
  doc.text(`${montant.toLocaleString("fr-FR")} FCFA`, M + 6, y + 17);
  doc.setFont("helvetica", "italic").setFontSize(8.5);
  const lettres = montantEnLettres(montant);
  doc.text(`(${lettres.charAt(0).toUpperCase() + lettres.slice(1)})`, W - M - 6, y + 13, { align: "right", maxWidth: 88 });

  // ── Texte légal ──
  y = 186;
  doc.setTextColor(60, 80, 100).setFont("helvetica", "normal").setFontSize(10);
  const texte =
    `Je soussigné, IAM Business Group, bailleur, reconnais avoir reçu de ${r.tenantName || "—"} ` +
    `la somme de ${montant.toLocaleString("fr-FR")} FCFA (${lettres}) au titre du loyer de la ` +
    `chambre ${r.number || "—"}, pour la période du 1er au dernier jour du mois de ${moisLabel}. ` +
    `Cette quittance annule tous les reçus qui auraient pu être établis précédemment pour la même période. ` +
    `Elle est délivrée sous réserve de tous droits du bailleur.`;
  doc.text(doc.splitTextToSize(texte, W - 2 * M), M, y, { lineHeightFactor: 1.55 });

  // ── Signature ──
  y = 232;
  doc.setFontSize(10).setTextColor(...GREY).setFont("helvetica", "normal");
  doc.text(`Fait à Koulikoro, le ${fmtDate(p.confirmedAt || new Date())}`, M, y);
  doc.setFont("helvetica", "bold").setTextColor(...NAVY);
  doc.text("Pour IAM Business Group", W - M - 55, y);
  doc.setDrawColor(...GREY).setLineWidth(0.3).line(W - M - 55, y + 22, W - M, y + 22);
  doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...GREY);
  doc.text("Signature et cachet", W - M - 55, y + 27);

  // ── Pied de page ──
  doc.setFontSize(7.5).setTextColor(150, 165, 180);
  doc.text(
    `Document généré automatiquement par l'application IAM Business Group · ${numero} · iam-business-group.web.app`,
    W / 2, 288, { align: "center" }
  );

  doc.save(`Quittance_${numero}.pdf`);
  return numero;
}
